import React, { FC } from 'react'
import { Button, CardContent, Chip } from '@mui/material'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import { Typography, CustomCardHeader } from '@dg-shared'
import { useTranslate } from '../../hooks/shared'
import { TFunction } from '../../hooks/shared/useTranslate'
import { addSpaceRegNum } from '@dg-util'
import { PolicyV2Type } from '../../types/ApiV2'
import { TRANSLATIONS as T } from '../../constants'
import { PAYMENT_METHOD_AVTALEGIRO } from '../../constants/auth-constants'
import { ProductName } from '../../types/interface/quote.interface'
import PolicyInsuranceDatesText from '@dg-shared/PolicyInsuranceDatesText'
import { formatWithThousandSpace } from '../../service/util.service'
import BaseCard, { useCardStyles } from './BaseCard'
import { makeStyles } from 'tss-react/mui'
import { Theme, alpha } from '@mui/material/styles'
import { PolicyStatus } from '../../FSM/shared/constants'

const useStyles = makeStyles()((theme: Theme) => ({
  cardHeaderContainerStyle: {
    padding: theme.spacing(2.5, 3),
    '& > div': { marginTop: 0 },
    '& h2': { fontWeight: 700, lineHeight: 1 },
    ...theme.brand.assets?.layout?.policyCardV2CardHeaderContainer,
  },

  mileageButton: {
    textTransform: 'none',
    width: '100%',
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(4),
    padding: theme.spacing(1.5, 3),
    fontWeight: 700,
    fontSize: '1rem',
  },

  avtalegiroButton: {
    textTransform: 'none',
    width: '100%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.success.contrastText,
    borderRadius: theme.spacing(4),
    padding: theme.spacing(1.5, 3),
    fontWeight: 700,
    fontSize: '1rem',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },

  buttonWrapper: {
    marginTop: theme.spacing(2),
    '&:first-of-type': {
      marginTop: 'auto',
      paddingTop: theme.spacing(2),
    },
  },

  pendingChip: {
    alignSelf: 'flex-end',
    position: 'absolute',
    fontWeight: 600,
    backgroundColor: alpha(theme.palette.warning.main, 0.12),
    color: theme.palette.warning.dark,
    border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
    '& .MuiChip-icon': {
      color: theme.palette.warning.dark,
    },
  },
}))

export interface PolicyCardProps {
  data: PolicyV2Type
  handleClick(): void
  onMileageClick: () => void
  onCreateAvtalegiro?: () => void
}

type PolicyCardConfig = { title: string; subtitle: string }

const joinNonEmpty = (parts: Array<string | number | undefined | null>) =>
  parts.filter(Boolean).join(' ')

const buildVehicleConfig = (data: PolicyV2Type): PolicyCardConfig => ({
  title: joinNonEmpty([data.CarMake, data.CarModel]),
  subtitle: data.MotorRegistrationNumber ? addSpaceRegNum(data.MotorRegistrationNumber) : '',
})

const buildTravelConfig = (data: PolicyV2Type, t: TFunction): PolicyCardConfig => ({
  title: data.TravelCoverage
    ? t(`productProps.${data.TravelCoverage}`, {}, 'string')
    : data.AgreementName || '',
  subtitle: data.TravelCoverArea ? t(`productProps.${data.TravelCoverArea}`, {}, 'string') : '',
})

const buildContentConfig = (data: PolicyV2Type): PolicyCardConfig => ({
  title: data.Address1 || data.AgreementName || '',
  subtitle: data.RoundedAnnualPremium
    ? `${formatWithThousandSpace(data.RoundedAnnualPremium)} kr`
    : '',
})

const PolicyCard: FC<PolicyCardProps> = ({
  data,
  handleClick,
  onMileageClick,
  onCreateAvtalegiro,
}) => {
  const { classes } = useStyles()
  const { classes: cardClasses } = useCardStyles()
  const { t } = useTranslate(T.POLICY_REVIEW)

  const configByProduct: Record<ProductName, PolicyCardConfig> = {
    [ProductName.VEHICLE]: buildVehicleConfig(data),
    [ProductName.TRAVEL]: buildTravelConfig(data, t),
    [ProductName.CONTENT]: buildContentConfig(data),
  }

  const { title, subtitle } = configByProduct[data.productName]
  const showMileageButton = data.productName === ProductName.VEHICLE && data.NeedsOdometerAdjustment
  const showAvtalegiroButton =
    !!onCreateAvtalegiro && data.PaymentMethod !== PAYMENT_METHOD_AVTALEGIRO
  // DFE-4116: pre-payment/future-dated policies are now listed instead of hidden — label
  // them so they don't read as a fully active policy. "Pending" is an engineering call,
  // not a business one; change the copy if business wants something more specific later.
  // Date-driven (not status-driven): i2i doesn't reliably mark a future-dated, unpaid
  // policy as PolicyStatus.PENDING at creation — it can still be ACCEPTED — so this
  // mirrors the same InsuredFrom-in-the-future check isCurrentActivePolicy uses to gate
  // editing, with the literal PENDING status kept as a fallback.
  const isNotYetStarted = new Date(data.InsuredFrom).getTime() > Date.now()
  const isPending = isNotYetStarted || data.PolicyStatus === PolicyStatus.PENDING

  return (
    <BaseCard
      header={
        <CustomCardHeader
          titleTextType='h2'
          withPrice={false}
          title={title}
          containerClass={classes.cardHeaderContainerStyle}
        />
      }
      onClick={handleClick}
      testId='policy-card'
    >
      <CardContent className={cardClasses.body}>
        {!!subtitle && (
          <Typography type='h3' className={cardClasses.subtitle} testId='card-subtitle'>
            {subtitle}
          </Typography>
        )}
        {isPending && (
          <Chip
            icon={<ScheduleRoundedIcon />}
            label={t('card.pendingLabel')}
            size='small'
            className={classes.pendingChip}
            data-testid='policy-pending-label'
          />
        )}
        <PolicyInsuranceDatesText policy={data} classDate={cardClasses.subtitle} />
        {showMileageButton && (
          <div className={classes.buttonWrapper}>
            <Button
              className={classes.mileageButton}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                onMileageClick()
              }}
              data-testid={'send-mileage-btn'}
            >
              {t('card.sendMileage')}
            </Button>
          </div>
        )}
        {showAvtalegiroButton && (
          <div className={classes.buttonWrapper}>
            <Button
              className={classes.avtalegiroButton}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                onCreateAvtalegiro()
              }}
              data-testid={'create-avtalegiro-btn'}
            >
              {t('card.createAvtalegiro')}
            </Button>
          </div>
        )}
      </CardContent>
    </BaseCard>
  )
}

export default PolicyCard
